import { useEffect, useState } from 'react';
import { adminApi, type AdminChartSummary } from '@/admin/adminApi';

export function AdminChartsPage() {
  const [charts, setCharts] = useState<AdminChartSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'verified' | 'rejected'>('all');

  async function reload() {
    setLoading(true);
    const res = await adminApi.listCharts();
    if (res.success) setCharts(res.data);
    setLoading(false);
  }

  useEffect(() => {
    reload();
  }, []);

  async function handleVerify(chartId: string, decision: 'verified' | 'rejected') {
    setBusyId(chartId);
    await adminApi.verifyChart(chartId, decision);
    await reload();
    setBusyId(null);
  }

  async function handleDelete(chartId: string) {
    if (!window.confirm('Delete this chart and all of its size rows? This cannot be undone.')) return;
    setBusyId(chartId);
    await adminApi.deleteChart(chartId);
    await reload();
    setBusyId(null);
  }

  const visible = charts.filter((c) => filter === 'all' || c.verificationStatus === filter);

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink">Charts</h2>
        <p className="mt-1 text-sm text-muted">
          Every size chart currently in the database, newest first. A brand only becomes selectable for a
          given audience/category once at least one of its charts here is verified. CSV Import is the way to
          add new charts — see the CSV Import tab.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(['all', 'pending', 'verified', 'rejected'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`min-h-[36px] rounded-full px-3 py-1 text-xs font-semibold capitalize transition-colors ${
              filter === f ? 'bg-brand text-ink' : 'bg-stone-100 text-muted'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted">No charts match this filter.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-stone-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-stone-50 text-xs uppercase text-muted">
              <tr>
                <th className="px-3 py-2">Brand</th>
                <th className="px-3 py-2">Audience</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Garment type</th>
                <th className="px-3 py-2">Rows</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {visible.map((c) => (
                <tr key={c.id}>
                  <td className="px-3 py-2 font-medium text-ink">
                    {c.brandName}
                    <span className="ml-1 font-mono text-xs text-muted">v{c.version}</span>
                  </td>
                  <td className="px-3 py-2 capitalize text-muted">{c.audience}</td>
                  <td className="px-3 py-2 capitalize text-muted">{c.category}</td>
                  <td className="px-3 py-2 text-muted">{c.garmentType ?? 'General'}</td>
                  <td className="px-3 py-2 text-muted">{c.rowCount}</td>
                  <td className="px-3 py-2">
                    {c.sourceUrl ? (
                      <a
                        href={c.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand underline underline-offset-2"
                      >
                        link
                      </a>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        c.verificationStatus === 'verified'
                          ? 'bg-brand/10 text-brand'
                          : c.verificationStatus === 'rejected'
                            ? 'bg-danger/10 text-danger'
                            : 'bg-stone-100 text-muted'
                      }`}
                    >
                      {c.verificationStatus}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1.5">
                      {c.verificationStatus !== 'verified' && (
                        <button
                          type="button"
                          disabled={busyId === c.id}
                          onClick={() => handleVerify(c.id, 'verified')}
                          className="min-h-[32px] rounded-lg bg-brand px-2.5 text-xs font-semibold text-ink disabled:opacity-40"
                        >
                          Verify
                        </button>
                      )}
                      {c.verificationStatus !== 'rejected' && (
                        <button
                          type="button"
                          disabled={busyId === c.id}
                          onClick={() => handleVerify(c.id, 'rejected')}
                          className="min-h-[32px] rounded-lg border border-stone-200 px-2.5 text-xs font-semibold text-ink disabled:opacity-40"
                        >
                          Reject
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() => handleDelete(c.id)}
                        className="min-h-[32px] rounded-lg border border-danger/30 px-2.5 text-xs font-semibold text-danger disabled:opacity-40"
                      >
                        Delete
                      </button>
                    </div>
                    {c.notes && <p className="mt-1 max-w-xs text-xs text-muted">{c.notes}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
